import useAnalyticsPage from '../../web-analytics/hooks/useAnalyticsPage';
import { Page } from '../../web-analytics/Page';
import React, { useState } from 'react';
import { useFetchedBillingInvoices } from '../hooks/useFetchedBillingInvoices';
import { useFetchedBillingCustomer } from '../hooks/useFetchedBillingCustomer';
import { useFetchedBillingPaymentMethods } from '../hooks/useFetchedBillingPaymentMethods';
import { BillingDetailsSection } from '../components/BillingDetailsSection';
import { InvoiceTable } from '../components/InvoiceTable';
import { PaymentMethodsTable } from '../components/PaymentMethodsTable';
import { PlanBenefits } from '../components/PlanBenefits';
import { BillingHeader } from '../components/BillingHeader';
import { INVOICE_TYPE_ISSUED, IssuedInvoice } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { UpgradeToAnnualPlan } from '../components/UpgradeToAnnualPlan';
import { CancellationWizard } from '../components/cancellation/CancellationWizard';
import { useFinalizeInvoicesMutation } from '../hooks/useFinalizeInvoicesMutation';
import { FullScreenSpinner } from '../../commons/components/Spinner';

export const BillingPortal = () => {
  useAnalyticsPage(Page.BILLING);

  const [cancellationWizardKey, setCancellationWizardKey] = useState<string | null>(null);

  const {
    queryStatusComponent: customerStatusComponent,
    query: { data: customer, isLoading: isCustomerLoading, isError: isCustomerFetchError },
  } = useFetchedBillingCustomer({ withTracking: true, refetchOnWindowFocus: 'always' });

  const {
    queryStatusComponent: invoicesStatusComponent,
    query: { data: invoices, isLoading: isInvoicesLoading, isError: isInvoicesFetchError },
  } = useFetchedBillingInvoices({ withTracking: true, refetchOnWindowFocus: 'always' });

  const {
    queryStatusComponent: paymentMethodsStatusComponent,
    query: {
      data: paymentMethodsFetchData,
      isLoading: isPaymentMethodsLoading,
      isError: isPaymentMethodsFetchError,
    },
  } = useFetchedBillingPaymentMethods({ withTracking: true });

  const [expanded, setExpanded] = useState(false);

  const { mutate: finalizeInvoices, status: finalizeInvoicesStatus } =
    useFinalizeInvoicesMutation();

  if ((isCustomerLoading || isCustomerFetchError) && !customer)
    return <div className="billing-error">{customerStatusComponent}</div>;
  if ((isInvoicesLoading || isInvoicesFetchError) && !invoices)
    return <div className="billing-error">{invoicesStatusComponent}</div>;
  if ((isPaymentMethodsLoading || isPaymentMethodsFetchError) && !paymentMethodsFetchData)
    return <div className="billing-error">{paymentMethodsStatusComponent}</div>;

  return (
    <div className="billing-container">
      <>
        <div>
          {!!customer && !!invoices && (
            <div>
              <BillingHeader
                customer={customer}
                invoices={invoices.filter((x) => x.type === INVOICE_TYPE_ISSUED) as IssuedInvoice[]}
                onCancelClick={() => {
                  finalizeInvoices();
                  setCancellationWizardKey(uuidv4());
                }}
                onSeeAnnualPlan={() => setExpanded(true)}
              />
            </div>
          )}

          <PlanBenefits />

          {!!customer && (
            <div className="billing__upgrade-to-annual-wrapper">
              <UpgradeToAnnualPlan
                expanded={expanded}
                onExpandedChanged={setExpanded}
                hasPendingPlanChange={!!customer.pendingPlanChange}
                changePlanOptions={customer.changePlanOptions}
              />
            </div>
          )}

          {!!paymentMethodsFetchData && !!customer && (
            <div className="billing-section">
              <PaymentMethodsTable paymentMethods={paymentMethodsFetchData} customer={customer} />
            </div>
          )}

          {!!customer && (
            <div className="billing-section">
              <BillingDetailsSection customer={customer} />
            </div>
          )}

          {!!invoices && (
            <div className="billing-section">
              <InvoiceTable invoices={invoices} header="Invoices" context="all-invoices" />
            </div>
          )}
        </div>
        {cancellationWizardKey && finalizeInvoicesStatus === 'success' && (
          <CancellationWizard key={cancellationWizardKey} />
        )}
        {finalizeInvoicesStatus === 'loading' && <FullScreenSpinner />}
      </>
    </div>
  );
};
